import { useState } from "react";
import { ActionDefinition } from "../../../../../../model/workflows/action-definition";
import Button from "../../../../../../common/components/xepp/button/Button";
import styles from "./StepInstanceActions.module.scss";
import { useSubmitStepMutation } from "../../../../../../service/customersupport-service";
import { DynamicFieldItem } from "../../../../../../model/dynamic-forms";
import { ServiceRequestType } from "../../../../../../model/service-request/service-request";
import { FieldType } from "../../../../../../model/service-request/attribute";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../../../stores/slices/loader-slice";
import { getErrorMessage } from "../../../../../../utils/error-handler";
import { useNavigate } from "react-router-dom";
import { ApplicationRoutes } from "../../../../../../routes";
import SelectDepartmentModal from "./SelectDepartmentModal";

interface StepInstanceActionsProps {
	actions: ActionDefinition[] | null;
	stepInstanceId: string;
	additionalServiceRequest?: ServiceRequestType;
	formFieldValues: DynamicFieldItem[];
	takenActionId?: string | null;
	takenAction?: ActionDefinition | null;
}

const StepInstanceActions = ({ actions, stepInstanceId, additionalServiceRequest, formFieldValues, takenActionId }: StepInstanceActionsProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [submitStep] = useSubmitStepMutation();
	const [pendingAction, setPendingAction] = useState<ActionDefinition | null>(null);

	const isStepCompleted = !!takenActionId;

	const buildRequestBody = (action: ActionDefinition) => {
		const requestBody: any = {
			stepInstanceId,
			takenActionId: action.id,
		};

		// Build additionalServiceRequest if it exists and has form values
		if (additionalServiceRequest && formFieldValues.length > 0) {
			const sections = additionalServiceRequest.sectionDefinitions
				.filter(section => !section.isDeleted)
				.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
				.map(section => {
					const sectionAttributes = section.attributeDefinitions
						.filter(attr => attr.isEnabled !== false)
						.map(attrDef => {
							// Find the field value for this attribute
							const fieldItem = formFieldValues.find(fv => fv.fieldId === attrDef.id);

							if (!fieldItem) {
								return null;
							}

							const attribute: any = {
								attributeDefinitionId: attrDef.id,
							};

							// Set value based on field type
							if (
								fieldItem.fieldType === FieldType.Dropdown ||
								fieldItem.fieldType === FieldType.SingleSelect ||
								fieldItem.fieldType === FieldType.ConditionalDropdown
							) {
								if (fieldItem.selectedChildId || fieldItem.fieldValue) {
									attribute.selectedOptionIds = [fieldItem.selectedChildId || fieldItem.fieldValue];
								}
							} else if (fieldItem.fieldType === FieldType.DateTime) {
								if (fieldItem.fieldValue instanceof Date) {
									attribute.dateValue = fieldItem.fieldValue.toISOString();
								} else if (typeof fieldItem.fieldValue === "string") {
									attribute.dateValue = fieldItem.fieldValue;
								}
							} else if (fieldItem.fieldType === FieldType.Bool) {
								attribute.boolValue = fieldItem.fieldValue;
							} else if (fieldItem.fieldType === FieldType.Number) {
								attribute.doubleValue = typeof fieldItem.fieldValue === "number" ? fieldItem.fieldValue : parseFloat(fieldItem.fieldValue) || 0;
							} else if (fieldItem.fieldType === FieldType.File) {
								// File will be handled in FormData
								if (fieldItem.fieldValue instanceof File) {
									attribute.file = fieldItem.fieldValue;
								}
							} else {
								// Text, TextDescription, etc.
								attribute.stringValue = fieldItem.fieldValue?.toString() || "";
							}

							return attribute;
						})
						.filter(attr => attr !== null);

					if (sectionAttributes.length === 0) {
						return null;
					}

					return {
						sectionDefinitionId: section.id || "",
						attributes: sectionAttributes,
					};
				})
				.filter(section => section !== null);

			if (sections.length > 0) {
				requestBody.additionalServiceRequest = {
					serviceRequestId: additionalServiceRequest.id,
					requestTypeId: additionalServiceRequest.requestTypeId || "",
					sections,
				};
			}
		}

		return requestBody;
	};

	const handleActionClick = (action: ActionDefinition) => {
		if (isStepCompleted) return;

		const nextDepartments = action.nextDepartments ?? [];
		if (nextDepartments.length > 1) {
			setPendingAction(action);
			return;
		}

		submitAction(action, nextDepartments[0]?.id);
	};

	const submitAction = async (action: ActionDefinition, departmentId?: string) => {
		try {
			dispatch(showLoader());
			const requestBody = { ...buildRequestBody(action), ...(departmentId && { NextDepartmentId: departmentId }) };
			await submitStep(requestBody).unwrap();
			toast.success("Step submitted successfully");
			navigate(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.WORKFLOWS_MANAGMENT}`);
		} catch (error: any) {
			toast.error(getErrorMessage(error.data) ?? "Error submitting step");
		} finally {
			dispatch(hideLoader());
		}
	};

	const handleDepartmentConfirm = (departmentId: string) => {
		if (!pendingAction) return;
		setPendingAction(null);
		submitAction(pendingAction, departmentId);
	};

	if (!actions || actions.length === 0) {
		return (
			<div className={styles.actionsContainer}>
				<span>No actions available</span>
			</div>
		);
	}

	return (
		<>
			<div className={styles.actionsContainer}>
				{actions.map(action => {
					const isTakenAction = takenActionId === action.id;
					const isDisabled = isStepCompleted && !isTakenAction;

					return (
						<Button
							key={action.id}
							text={isTakenAction ? `${action.name} ✓` : action.name}
							onClick={() => handleActionClick(action)}
							variant={isTakenAction ? "success" : "secondary"}
							disabled={isDisabled}
							className={isTakenAction ? styles.takenAction : isDisabled ? styles.disabledAction : ""}
						/>
					);
				})}
			</div>

			<SelectDepartmentModal
				isOpen={!!pendingAction}
				onClose={() => setPendingAction(null)}
				onConfirm={handleDepartmentConfirm}
				departments={pendingAction?.nextDepartments ?? []}
			/>
		</>
	);
};

export default StepInstanceActions;
